import React, { useMemo } from 'react'
import { Checkbox } from './checkbox'
import { cn } from './cn'
import { Skeleton } from './skeleton'

export interface DataTableColumn<T> {
  key: string
  header: React.ReactNode
  render: (row: T) => React.ReactNode
  // value-list: local — CSS 對齊方向（text-align），跟句段時間戳邊界／日期範圍邊界同值純屬巧合
  align?: 'start' | 'end'
  width?: string
  /** Use for identifiers and dates; numeric cells already use mono. */
  mono?: boolean
}

export interface DataTableSelection {
  selected: Set<string>
  onChange: (next: Set<string>) => void
  label?: string
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  loading?: boolean
  /** Skeleton rows while loading. Defaults to the visible page size of 10. */
  pageSize?: number
  empty?: React.ReactNode
  onRowClick?: (row: T) => void
  selection?: DataTableSelection
  className?: string
}

/**
 * Stable table geometry for admin and back-office lists. It owns table chrome,
 * numeric alignment, loading rows and selection so page code supplies only
 * columns and data.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  pageSize = 10,
  empty,
  onRowClick,
  selection,
  className,
}: DataTableProps<T>) {
  const rowIds = useMemo(() => rows.map(rowKey), [rows, rowKey])
  const selectedCount = rowIds.filter((id) => selection?.selected.has(id)).length
  const allSelected = rowIds.length > 0 && selectedCount === rowIds.length
  const someSelected = selectedCount > 0 && !allSelected
  const columnCount = columns.length + (selection ? 1 : 0)

  const toggleAll = () => {
    if (!selection) return
    const next = new Set(selection.selected)
    for (const id of rowIds) {
      if (allSelected) next.delete(id)
      else next.add(id)
    }
    selection.onChange(next)
  }

  const toggleRow = (id: string) => {
    if (!selection) return
    const next = new Set(selection.selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    selection.onChange(next)
  }

  return (
    <div
      className={cn(
        'overflow-x-auto rounded-[var(--radius)] border border-border bg-background',
        className
      )}
    >
      {/* Stable handle for flow tests, same convention as SegmentedControl:
          class lists are implementation detail, `data-ui` is the contract. */}
      <table data-ui="data-table" className="w-full border-collapse text-left text-sm">
        <thead className="border-b border-border bg-surface text-xs text-muted">
          <tr>
            {selection ? (
              <th className="w-10 px-3 py-3">
                <Checkbox
                  aria-label={selection.label ?? 'Select all rows'}
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                />
              </th>
            ) : null}
            {columns.map((column) => (
              <th
                key={column.key}
                className={cn(
                  'whitespace-nowrap px-4 py-3 font-medium',
                  column.align === 'end' && 'text-end font-mono tabular-nums',
                  column.mono && 'font-mono'
                )}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: pageSize }, (_unused, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} className="border-b border-border last:border-b-0">
                  {selection ? (
                    <td className="px-3 py-3.5">
                      <Skeleton width={16} height={16} />
                    </td>
                  ) : null}
                  {columns.map((column, columnIndex) => (
                    <td key={column.key} className="px-4 py-3.5">
                      <Skeleton width={columnIndex === 0 ? '72%' : '48%'} />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => {
                const id = rowKey(row)
                const interactive = Boolean(onRowClick)
                return (
                  <tr
                    key={id}
                    className={cn(
                      'border-b border-border last:border-b-0',
                      interactive &&
                        'cursor-pointer transition-colors hover:bg-surface focus-visible:bg-surface'
                    )}
                    tabIndex={interactive ? 0 : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onKeyDown={
                      onRowClick
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              onRowClick(row)
                            }
                          }
                        : undefined
                    }
                  >
                    {selection ? (
                      <td
                        className="px-3 py-3.5"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <Checkbox
                          aria-label={`${selection.label ?? 'Select row'} ${id}`}
                          checked={selection.selected.has(id)}
                          onChange={() => toggleRow(id)}
                        />
                      </td>
                    ) : null}
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          'whitespace-nowrap px-4 py-3.5 text-foreground',
                          column.align === 'end' && 'text-end font-mono tabular-nums',
                          column.mono && 'font-mono'
                        )}
                      >
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                )
              })}
          {!loading && rows.length === 0 && empty ? (
            <tr>
              <td colSpan={columnCount} className="p-0">
                {empty}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}
